import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Modal, Tile, Tag, Button, ButtonSet } from '@carbon/react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Temperature, Humidity, Location, Time, PlayFilled, PauseFilled, StopFilled } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../services/supabaseClient';
import 'leaflet/dist/leaflet.css';
import './ZoneMapModal.scss';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
};

// Component to recenter map when zone changes
const RecenterMap = React.memo(({ center }) => {
  const map = useMap();
  React.useEffect(() => {
    if (center) {
      map.setView(center, 13);
    }
  }, [center, map]);
  return null;
});

const ZoneMapModal = React.memo(({ isOpen, onClose, zone, sensors }) => {
  const { theme } = useTheme();
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [gpsTracks, setGpsTracks] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // 1x, 2x, 3x, 5x
  const animationRef = useRef(null);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  // Fetch GPS tracks when modal opens
  useEffect(() => {
    let isMounted = true;

    if (isOpen && zone && sensors && sensors.length > 0) {
      fetchGPSTracks();
    } else {
      // Reset tracks when modal closes
      if (isMounted) {
        setGpsTracks([]);
        setCurrentTrackIndex(0);
        setIsPlaying(false);
      }
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, zone?.id, sensors?.length]);

  // Fetch GPS tracks for sensors in this zone
  const fetchGPSTracks = React.useCallback(async () => {
    if (!zone?.id || !sensors || sensors.length === 0) {
      setGpsTracks([]);
      setLoadingTracks(false);
      return;
    }

    setLoadingTracks(true);
    try {
      const sensorIds = sensors.map(s => s.id);

      const { data, error } = await supabase
        .from('gps_tracks')
        .select('*')
        .eq('zone_id', zone.id)
        .in('sensor_id', sensorIds)
        .order('recorded_at', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        setGpsTracks(data);
        console.log(`Loaded ${data.length} GPS track points for zone ${zone.name}`);
      } else {
        setGpsTracks([]);
      }
    } catch (error) {
      console.error('Error fetching GPS tracks:', error);
      setGpsTracks([]);
    } finally {
      setLoadingTracks(false);
    }
  }, [zone?.id, zone?.name, sensors]);

  // Get zone center coordinates
  const zoneCenter = useMemo(() => {
    if (zone && zone.latitude && zone.longitude) {
      return [zone.latitude, zone.longitude];
    }
    // Default to a central location if no coordinates
    return [40.7128, -74.0060]; // New York
  }, [zone]);

  // Animation loop
  useEffect(() => {
    if (isPlaying && gpsTracks.length > 0) {
      const baseInterval = 500; // Base speed: 500ms between points
      const interval = baseInterval / playbackSpeed;

      animationRef.current = setInterval(() => {
        setCurrentTrackIndex((prevIndex) => {
          if (prevIndex >= gpsTracks.length - 1) {
            // Reached end of track
            setIsPlaying(false);
            return prevIndex;
          }
          return prevIndex + 1;
        });
      }, interval);
    }

    // Cleanup function
    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isPlaying, playbackSpeed, gpsTracks.length]);

  // Cleanup animation when modal closes
  useEffect(() => {
    if (!isOpen) {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
      setIsPlaying(false);
      setCurrentTrackIndex(0);
    }
  }, [isOpen]);

  // Playback controls
  const handlePlay = React.useCallback(() => {
    if (currentTrackIndex >= gpsTracks.length - 1) {
      // If at end, restart from beginning
      setCurrentTrackIndex(0);
    }
    setIsPlaying(true);
  }, [currentTrackIndex, gpsTracks.length]);

  const handlePause = React.useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleStop = React.useCallback(() => {
    setIsPlaying(false);
    setCurrentTrackIndex(0);
  }, []);

  const handleSpeedChange = React.useCallback((speed) => {
    setPlaybackSpeed(speed);
  }, []);

  // Get track path for polyline
  const trackPath = useMemo(() => {
    if (gpsTracks.length === 0) return [];
    return gpsTracks.map(track => [
      parseFloat(track.latitude),
      parseFloat(track.longitude)
    ]);
  }, [gpsTracks]);

  // Get current position during playback
  const currentPosition = useMemo(() => {
    if (gpsTracks.length === 0 || currentTrackIndex >= gpsTracks.length) return null;
    const track = gpsTracks[currentTrackIndex];
    return {
      lat: parseFloat(track.latitude),
      lng: parseFloat(track.longitude),
      data: track
    };
  }, [gpsTracks, currentTrackIndex]);

  // Get sensors with valid coordinates
  const sensorsWithLocation = useMemo(() => {
    if (!sensors || sensors.length === 0) return [];

    return sensors.filter(sensor => {
      // Check if sensor has location data
      // Sensors might inherit zone location or have their own
      return (sensor.latitude && sensor.longitude) ||
             (zone && zone.latitude && zone.longitude);
    }).map(sensor => ({
      ...sensor,
      // Use sensor's own location or fall back to zone location
      lat: sensor.latitude || zone?.latitude || zoneCenter[0],
      lng: sensor.longitude || zone?.longitude || zoneCenter[1],
    }));
  }, [sensors, zone, zoneCenter]);

  const formatTemperature = (temp) => {
    if (temp === null || temp === undefined) return '--.-°C';
    return `${temp.toFixed(1)}°C`;
  };

  const formatHumidity = (humidity) => {
    if (!humidity || humidity <= 0) return 'No data';
    return `${humidity.toFixed(1)}%`;
  };

  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return 'No data';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getSensorStatus = (sensor) => {
    if (!sensor.latest_reading) return { type: 'gray', text: 'No Data' };

    const lastUpdate = new Date(sensor.latest_reading.recorded_at);
    const now = new Date();
    const diffMins = (now - lastUpdate) / 60000;

    // If negative minutes (timestamp in future due to timezone/clock skew), treat as offline
    if (diffMins < 0 || diffMins > 60) return { type: 'red', text: 'Offline' };
    if (diffMins > 30) return { type: 'orange', text: 'Warning' };
    return { type: 'green', text: 'Active' };
  };

  const handleSensorClick = React.useCallback((sensor) => {
    setSelectedSensor(sensor);
  }, []);

  if (!zone) return null;

  return (
    <Modal
      open={isOpen}
      onRequestClose={onClose}
      modalHeading={`${zone.name} - Map View`}
      primaryButtonText="Close"
      onRequestSubmit={onClose}
      size="lg"
      className="zone-map-modal"
    >
      <div className="zone-map-modal__content" style={{ backgroundColor: bgPrimary }}>
        {/* Map Section (Left) */}
        <div className="zone-map-modal__map-container">
          {zone.latitude && zone.longitude ? (
            <>
              <MapContainer
                center={zoneCenter}
                zoom={13}
                style={{ height: '100%', width: '100%', borderRadius: '8px' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url={isDark
                    ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                    : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                  }
                />
                <RecenterMap center={zoneCenter} />

              {/* Zone location marker */}
              <Marker
                position={zoneCenter}
                icon={createCustomIcon('#0f62fe')}
              >
                <Popup>
                  <div style={{ padding: '8px' }}>
                    <strong>{zone.name}</strong>
                    <br />
                    {zone.address_formatted || zone.addressFormatted || 'Zone Location'}
                  </div>
                </Popup>
              </Marker>

              {/* Sensor markers (only show if not playing track) */}
              {!isPlaying && sensorsWithLocation.map((sensor) => (
                <Marker
                  key={sensor.id}
                  position={[sensor.lat, sensor.lng]}
                  icon={createCustomIcon('#24a148')}
                  eventHandlers={{
                    click: () => handleSensorClick(sensor),
                  }}
                >
                  <Popup>
                    <div style={{ padding: '8px' }}>
                      <strong>{sensor.name}</strong>
                      <br />
                      {sensor.latest_reading ? (
                        <>
                          Temp: {formatTemperature(sensor.latest_reading.temperature)}
                          <br />
                          Humidity: {formatHumidity(sensor.latest_reading.humidity)}
                        </>
                      ) : (
                        'No data'
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* GPS Track Path */}
              {trackPath.length > 0 && (
                <Polyline
                  positions={trackPath}
                  color="#ff6b35"
                  weight={4}
                  opacity={0.7}
                />
              )}

              {/* Animated Current Position Marker */}
              {currentPosition && (
                <Marker
                  position={[currentPosition.lat, currentPosition.lng]}
                  icon={createCustomIcon('#ff6b35')}
                >
                  <Popup>
                    <div style={{ padding: '8px' }}>
                      <strong>Current Position</strong>
                      <br />
                      Temp: {formatTemperature(currentPosition.data.temperature)}
                      <br />
                      Humidity: {formatHumidity(currentPosition.data.humidity)}
                      <br />
                      Speed: {currentPosition.data.speed ? `${currentPosition.data.speed} km/h` : 'N/A'}
                      <br />
                      Time: {new Date(currentPosition.data.recorded_at).toLocaleTimeString()}
                    </div>
                  </Popup>
                </Marker>
              )}
              </MapContainer>

              {/* Loading GPS Tracks Indicator */}
              {loadingTracks && (
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  backgroundColor: bgPrimary,
                  border: `1px solid ${borderColor}`,
                  borderRadius: '8px',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  zIndex: 1000,
                }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #0f62fe',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }} />
                  <span style={{ color: textPrimary, fontSize: '0.875rem' }}>
                    Loading GPS tracks...
                  </span>
                </div>
              )}
            </>
          ) : (
            <div style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: bgSecondary,
              borderRadius: '8px',
              padding: '32px',
              textAlign: 'center',
            }}>
              <div>
                <Location size={48} style={{ color: textSecondary, marginBottom: '16px' }} />
                <p style={{ color: textPrimary, fontWeight: 500, marginBottom: '8px' }}>
                  No Location Data
                </p>
                <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
                  This zone doesn't have location coordinates set.
                  <br />
                  Edit the zone to add an address.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Sensors List Section (Right) */}
        <div className="zone-map-modal__sensors-list" style={{ backgroundColor: bgSecondary }}>
          <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '16px', padding: '0 16px' }}>
            Sensors in Zone ({sensorsWithLocation.length})
          </h3>

          {sensorsWithLocation.length === 0 ? (
            <div style={{
              padding: '32px 16px',
              textAlign: 'center',
              color: textSecondary,
            }}>
              <p>No sensors assigned to this zone</p>
            </div>
          ) : (
            <div className="zone-map-modal__sensors-scroll">
              {sensorsWithLocation.map((sensor) => {
                const status = getSensorStatus(sensor);
                const isSelected = selectedSensor?.id === sensor.id;

                return (
                  <Tile
                    key={sensor.id}
                    style={{
                      backgroundColor: isSelected ? (isDark ? '#393939' : '#e0e0e0') : bgPrimary,
                      border: `1px solid ${isSelected ? '#0f62fe' : borderColor}`,
                      padding: '16px',
                      marginBottom: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => handleSensorClick(sensor)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ color: textPrimary, fontWeight: 600, marginBottom: '4px', fontSize: '0.9375rem' }}>
                          {sensor.name}
                        </p>
                        <p style={{ color: textSecondary, fontSize: '0.75rem' }}>
                          ID: {sensor.device_id || sensor.id}
                        </p>
                      </div>
                      <Tag type={status.type} size="sm">
                        {status.text}
                      </Tag>
                    </div>

                    {sensor.latest_reading ? (
                      <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <Temperature size={14} style={{ color: '#0f62fe' }} />
                            <span style={{ color: textSecondary, fontSize: '0.75rem' }}>Temp</span>
                          </div>
                          <p style={{ color: textPrimary, fontSize: '0.9375rem', fontWeight: 500 }}>
                            {formatTemperature(sensor.latest_reading.temperature)}
                          </p>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <Humidity size={14} style={{ color: '#24a148' }} />
                            <span style={{ color: textSecondary, fontSize: '0.75rem' }}>Humidity</span>
                          </div>
                          <p style={{ color: textPrimary, fontSize: '0.9375rem', fontWeight: 500 }}>
                            {formatHumidity(sensor.latest_reading.humidity)}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p style={{ color: textSecondary, fontSize: '0.875rem', marginTop: '8px' }}>
                        No recent data
                      </p>
                    )}

                    {sensor.latest_reading && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${borderColor}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Time size={12} style={{ color: textSecondary }} />
                          <span style={{ color: textSecondary, fontSize: '0.75rem' }}>
                            {formatLastUpdated(sensor.latest_reading.recorded_at)}
                          </span>
                        </div>
                      </div>
                    )}
                  </Tile>
                );
              })}
            </div>
          )}

          {/* GPS Track Playback Controls */}
          {gpsTracks.length > 0 && (
            <div
              className="zone-map-modal__playback-controls"
              style={{
                backgroundColor: bgPrimary,
                border: `1px solid ${borderColor}`,
                borderTop: `2px solid ${borderColor}`,
                borderRadius: '0',
                padding: '16px',
                marginTop: 'auto',
              }}
            >
              <h4 style={{ color: textPrimary, fontSize: '0.875rem', fontWeight: 600, marginBottom: '12px' }}>
                GPS Track Playback
              </h4>

              {/* Playback Buttons */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <Button
                  kind="tertiary"
                  size="sm"
                  renderIcon={PlayFilled}
                  onClick={handlePlay}
                  disabled={isPlaying}
                  hasIconOnly
                  iconDescription="Play"
                />
                <Button
                  kind="tertiary"
                  size="sm"
                  renderIcon={PauseFilled}
                  onClick={handlePause}
                  disabled={!isPlaying}
                  hasIconOnly
                  iconDescription="Pause"
                />
                <Button
                  kind="tertiary"
                  size="sm"
                  renderIcon={StopFilled}
                  onClick={handleStop}
                  disabled={currentTrackIndex === 0 && !isPlaying}
                  hasIconOnly
                  iconDescription="Stop"
                />
              </div>

              {/* Speed Controls */}
              <div style={{ marginBottom: '12px' }}>
                <span style={{ color: textSecondary, fontSize: '0.75rem', display: 'block', marginBottom: '6px' }}>
                  Playback Speed
                </span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {[1, 2, 3, 5].map((speed) => (
                    <Button
                      key={speed}
                      kind={playbackSpeed === speed ? 'primary' : 'ghost'}
                      size="sm"
                      onClick={() => handleSpeedChange(speed)}
                      style={{
                        flex: 1,
                      }}
                    >
                      {speed}x
                    </Button>
                  ))}
                </div>
              </div>

              {/* Progress Indicator */}
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '6px',
                }}>
                  <span style={{ color: textSecondary, fontSize: '0.75rem' }}>
                    Point {currentTrackIndex + 1} of {gpsTracks.length}
                  </span>
                  {currentPosition && (
                    <span style={{ color: textSecondary, fontSize: '0.75rem' }}>
                      {new Date(currentPosition.data.recorded_at).toLocaleTimeString()}
                    </span>
                  )}
                </div>
                <div style={{
                  height: '6px',
                  backgroundColor: borderColor,
                  borderRadius: '3px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${((currentTrackIndex + 1) / gpsTracks.length) * 100}%`,
                    backgroundColor: '#0f62fe',
                    transition: 'width 0.2s ease',
                  }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.zone?.id === nextProps.zone?.id &&
    prevProps.sensors?.length === nextProps.sensors?.length &&
    prevProps.onClose === nextProps.onClose
  );
});

ZoneMapModal.displayName = 'ZoneMapModal';

export default ZoneMapModal;
